import { useEffect, useRef, useState } from 'react';

type Target = { churchId: string; presentationId: string } | { token: string };

/**
 * Socket de Presentación: avisa al instante cuando algo cambia para volver a pedir el estado.
 * Devuelve si el aviso está activo; mientras no lo esté, las pantallas siguen preguntando cada
 * segundo. Si se cae, reintenta con espera creciente.
 */
export function usePresentationSocket(target: Target | null, onChange: () => void): boolean {
  const [watching, setWatching] = useState(false);
  const handler = useRef(onChange);
  handler.current = onChange;
  const key = target ? JSON.stringify(target) : null;

  useEffect(() => {
    if (!key || typeof WebSocket === 'undefined') return;
    let ws: WebSocket | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const connect = () => {
      ws = new WebSocket(`${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/presentation/ws`);
      ws.onopen = () => ws?.send(JSON.stringify({ type: 'watch', ...(JSON.parse(key) as Target) }));
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(String(event.data)) as { type: string };
          if (data.type === 'watching') {
            retry = 0;
            setWatching(true);
            handler.current(); // lo que haya cambiado mientras se conectaba
          } else if (data.type === 'changed') handler.current();
        } catch {
          // Mensaje no reconocido: se ignora.
        }
      };
      ws.onclose = () => {
        setWatching(false);
        if (stopped) return;
        retry += 1;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(retry, 5)));
      };
    };
    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [key]);

  return watching;
}
